/**
 * HF Delivery Countdown — live timer synced to planners.dand3.com delivery config.
 * Keeps Hello Flowers block styling; logic mirrors wf-delivery-countdown.js (WF).
 */
(function () {
  'use strict';

  var CACHE_KEY = 'hf_dc_config';
  var CACHE_TTL = 5 * 60 * 1000;
  var AVAILABILITY_TTL = 60 * 1000;
  var REFRESH_MS = 60 * 1000;
  var API_BASE = 'https://planners.dand3.com';
  var STORE_KEY = 'HF';

  var SGT = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Singapore',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });

  function sgtParts(now) {
    var parts = SGT.formatToParts(now || new Date());
    var o = {};
    for (var i = 0; i < parts.length; i++) o[parts[i].type] = Number(parts[i].value) || 0;
    return o;
  }

  function sgtDateStr(now) {
    var p = sgtParts(now);
    return String(p.year).padStart(4, '0') + '-' +
      String(p.month).padStart(2, '0') + '-' +
      String(p.day).padStart(2, '0');
  }

  function dow(dateStr) {
    return new Date(dateStr + 'T12:00:00+08:00').getUTCDay();
  }

  function addDays(dateStr, n) {
    var d = new Date(dateStr + 'T12:00:00+08:00');
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  }

  function sgtEpoch(dateStr, hour, minute) {
    var parts = dateStr.split('-').map(Number);
    return Date.UTC(parts[0], parts[1] - 1, parts[2], (hour || 0) - 8, minute || 0, 0);
  }

  function fmtTime(h, m) {
    var hr = h % 12 || 12;
    var ap = h < 12 ? 'am' : 'pm';
    return m === 0 ? hr + ap : hr + ':' + String(m).padStart(2, '0') + ap;
  }

  function hfSlotWindow(s) {
    var sh = s.startHour || 0;
    var eh = s.endHour || 0;
    var startStr = sh % 12 || 12;
    var endStr = eh % 12 || 12;
    var endMer = eh < 12 ? 'am' : 'pm';
    if (Math.floor(sh / 12) === Math.floor(eh / 12)) {
      return startStr + '-' + endStr + endMer;
    }
    return fmtTime(sh, s.startMinute || 0) + '-' + fmtTime(eh, s.endMinute || 0);
  }

  function fmtCutoffHf(h, m) {
    return new Intl.DateTimeFormat('en-SG', {
      timeZone: 'Asia/Singapore',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(sgtEpoch(sgtDateStr(new Date()), h, m))).toLowerCase();
  }

  function fmtHfCountdown(ms) {
    if (ms <= 0) return '00:00:00';
    var totalSeconds = Math.floor(ms / 1000);
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    return String(hours).padStart(2, '0') + ':' +
      String(minutes).padStart(2, '0') + ':' +
      String(seconds).padStart(2, '0');
  }

  function isBlackout(cfg, d) {
    var list = cfg.blackout_dates || [];
    for (var i = 0; i < list.length; i++) if (list[i].date === d) return true;
    return false;
  }

  function getOverride(cfg, d) {
    var list = cfg.override_days || [];
    for (var i = 0; i < list.length; i++) if (list[i].date === d) return list[i];
    return null;
  }

  function allSlotsForDate(cfg, dateStr) {
    if (isBlackout(cfg, dateStr)) return [];
    var ov = getOverride(cfg, dateStr);
    if (ov && ov.delivery && ov.delivery.slots) return ov.delivery.slots;
    var d = dow(dateStr);
    return (cfg.timeslots || []).filter(function (s) {
      return s.availableDays && s.availableDays.indexOf(d) >= 0;
    });
  }

  function availabilityMap(availability) {
    var map = {};
    var list = availability && Array.isArray(availability.slots) ? availability.slots : null;
    if (!list) return null;
    for (var i = 0; i < list.length; i++) {
      map[list[i].slotId] = list[i].available === true && list[i].visible !== false;
    }
    return map;
  }

  function filterAvailableSlots(slots, availability) {
    if (availability && availability.blackout) return [];
    var map = availabilityMap(availability);
    if (!map) return slots;
    return slots.filter(function (slot) { return map[slot.id] === true; });
  }

  function slotsBeforeCutoff(cfg, dateStr, availability, now) {
    var slots = allSlotsForDate(cfg, dateStr);
    var nowEpoch = (now || new Date()).getTime();
    return filterAvailableSlots(slots, availability).filter(function (s) {
      return nowEpoch < sgtEpoch(dateStr, s.cutoffHour || 0, s.cutoffMinute || 0);
    });
  }

  function buildStateFromSlots(dateStr, dayOffset, openSlots, current) {
    var nowEpoch = current.getTime();
    var slot = openSlots[0];
    var cutoffAt = sgtEpoch(dateStr, slot.cutoffHour || 0, slot.cutoffMinute || 0);
    var label = 'same-day';
    if (dayOffset === 1) {
      label = 'tomorrow';
    } else if (dayOffset > 1) {
      label = new Date(dateStr + 'T12:00:00+08:00')
        .toLocaleDateString('en-SG', { weekday: 'long', timeZone: 'Asia/Singapore' });
    }
    return {
      remaining: cutoffAt - nowEpoch,
      cutoffAt: cutoffAt,
      cutoff: fmtCutoffHf(slot.cutoffHour || 0, slot.cutoffMinute || 0),
      window: hfSlotWindow(slot),
      dayLabel: label,
      date: dateStr,
    };
  }

  function computeState(cfg, availabilityByDate, now) {
    var current = now || new Date();
    var today = sgtDateStr(current);
    for (var i = 0; i <= 7; i++) {
      var dateStr = addDays(today, i);
      var openSlots = slotsBeforeCutoff(cfg, dateStr, availabilityByDate && availabilityByDate[dateStr], current);
      if (openSlots.length > 0) return buildStateFromSlots(dateStr, i, openSlots, current);
    }
    return { noSlots: true };
  }

  function render(root, state) {
    var countdownEl = root.querySelector('[data-countdown]');
    var messageEl = root.querySelector('[data-message]');
    if (!countdownEl || !messageEl) return;

    if (!state || state.noSlots) {
      countdownEl.textContent = '00:00:00';
      messageEl.textContent = 'Check cart for next delivery slot';
      return;
    }

    var remaining = state.cutoffAt ? state.cutoffAt - Date.now() : state.remaining;
    countdownEl.textContent = fmtHfCountdown(remaining);

    if (state.dayLabel === 'same-day') {
      messageEl.textContent = 'Order by ' + state.cutoff + " for today's " + state.window + ' delivery';
    } else if (state.dayLabel === 'tomorrow') {
      messageEl.textContent = 'Order by ' + state.cutoff + " for tomorrow's " + state.window + ' delivery';
    } else {
      messageEl.textContent = 'Order by ' + state.cutoff + ' for ' + state.dayLabel + "'s " + state.window + ' delivery';
    }
  }

  function loadConfig(cb) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var c = JSON.parse(raw);
        if (c._ts && Date.now() - c._ts < CACHE_TTL) return cb(c.data);
      }
    } catch (e) { /* ignore */ }

    fetch(API_BASE + '/api/delivery/' + STORE_KEY + '/public?_t=' + Date.now(), {
      credentials: 'omit', cache: 'no-store',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ _ts: Date.now(), data: data }));
        } catch (e) { /* quota */ }
        cb(data);
      })
      .catch(function () { /* keep last render */ });
  }

  function loadAvailability(dateStr) {
    var key = 'hf_dc_availability_' + dateStr;
    try {
      var raw = sessionStorage.getItem(key);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached._ts && Date.now() - cached._ts < AVAILABILITY_TTL) return Promise.resolve(cached.data);
      }
    } catch (e) { /* ignore */ }

    return fetch(API_BASE + '/api/delivery/' + STORE_KEY + '/availability?date=' + encodeURIComponent(dateStr) + '&_t=' + Date.now(), {
      credentials: 'omit', cache: 'no-store',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data) {
          try {
            sessionStorage.setItem(key, JSON.stringify({ _ts: Date.now(), data: data }));
          } catch (e) { /* quota */ }
        }
        return data;
      })
      .catch(function () { return null; });
  }

  function findStateWithAvailability(cfg, index, availabilityByDate, now) {
    var today = sgtDateStr(now);
    if (index > 7) return Promise.resolve(computeState(cfg, availabilityByDate, now));

    var dateStr = addDays(today, index);
    if (allSlotsForDate(cfg, dateStr).length === 0) {
      return findStateWithAvailability(cfg, index + 1, availabilityByDate, now);
    }

    return loadAvailability(dateStr).then(function (availability) {
      availabilityByDate[dateStr] = availability;
      var current = now || new Date();
      var openSlots = slotsBeforeCutoff(cfg, dateStr, availability, current);
      if (openSlots.length > 0) {
        return buildStateFromSlots(dateStr, index, openSlots, current);
      }
      return findStateWithAvailability(cfg, index + 1, availabilityByDate, now);
    });
  }

  function initRoot(root) {
    loadConfig(function (cfg) {
      if (!cfg || !cfg.timeslots || cfg.timeslots.length === 0) return;

      var latestState = null;
      var refreshInFlight = false;

      function refreshState() {
        if (refreshInFlight) return;
        refreshInFlight = true;
        findStateWithAvailability(cfg, 0, {})
          .then(function (state) {
            latestState = state;
            render(root, latestState);
          })
          .finally(function () {
            refreshInFlight = false;
          });
      }

      function tick() {
        if (!latestState || (latestState.cutoffAt && Date.now() >= latestState.cutoffAt)) {
          refreshState();
          return;
        }
        render(root, latestState);
      }

      refreshState();
      setInterval(tick, 1000);
      setInterval(refreshState, REFRESH_MS);
    });
  }

  function init() {
    document.querySelectorAll('[data-hf-delivery-countdown]').forEach(initRoot);
  }

  window.HF_DELIVERY_COUNTDOWN = {
    _test: {
      computeState: computeState,
      render: render,
      hfSlotWindow: hfSlotWindow,
      fmtHfCountdown: fmtHfCountdown,
      sgtEpoch: sgtEpoch,
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
